'use client';

import React, { useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';
import { buttonVariants } from './button-variants';
import { useModalLayer } from './use-modal-layer';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Which edge the sheet slides in from. */
  side?: 'right' | 'left';
  /** Sheet title. Rendered in the header. */
  title?: string;
  /** Optional description below the title. */
  description?: string;
  /** Width preset. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Sheet body content. */
  children: React.ReactNode;
  /** Optional footer region (sticky at bottom). */
  footer?: React.ReactNode;
  /** Close on backdrop click. Default true. */
  closeOnBackdrop?: boolean;
  /** Close on Escape. Default true. */
  closeOnEscape?: boolean;
  /** Show X close button. Default true. */
  showCloseButton?: boolean;
  /** Extra classes applied to the sheet panel. */
  className?: string;
}

// The default uses the nearest named width until a 560px drawer token is available.
const sizeClasses: Record<'sm' | 'md' | 'lg' | 'xl', string> = {
  sm: 'w-full sm:max-w-sm',
  md: 'w-full sm:max-w-xl',
  lg: 'w-full sm:max-w-lg',
  xl: 'w-full sm:max-w-2xl',
};

/**
 * Side-sliding panel — used for secondary context (e.g., "Show guide",
 * inline record details, filters panel). Keeps primary surface visible.
 *
 * Pattern-wise a close cousin of Dialog but horizontally anchored. Uses
 * the same focus trap and Escape-to-close conventions.
 */
export function Sheet({
  open,
  onClose,
  side = 'right',
  title,
  description,
  size = 'md',
  children,
  footer,
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  className,
}: SheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const sheetId = useId();
  const titleId = `${sheetId}-title`;
  const descId = `${sheetId}-description`;

  useModalLayer({
    open,
    panelRef: sheetRef,
    onClose,
    closeOnEscape,
    focusableSelector:
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  });

  if (!open) return null;

  const sideClasses =
    side === 'right'
      ? 'right-0 sm:rounded-l-overlay border-l motion-reduce:animate-none animate-in slide-in-from-right duration-200'
      : 'left-0 sm:rounded-r-overlay border-r motion-reduce:animate-none animate-in slide-in-from-left duration-200';

  const content = (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descId : undefined}
    >
      <div
        className="fixed inset-0 bg-surface-inverse/40 backdrop-blur-sm motion-reduce:animate-none animate-in fade-in-0 duration-200"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={sheetRef}
        tabIndex={-1}
        className={cn(
          'fixed inset-y-0 flex flex-col bg-surface-sheet shadow-dialog border-border',
          sideClasses,
          sizeClasses[size],
          className,
        )}
      >
        {(title || showCloseButton) && (
          <div className="flex min-w-0 flex-wrap items-start gap-x-4 gap-y-3 shrink-0 border-b border-border px-6 py-4">
            <div className="min-w-0 flex-[1_1_16rem]">
              {title && (
                <h2
                  id={titleId}
                  className="min-w-0 break-normal [overflow-wrap:break-word] font-body text-card-title text-text-heading"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p id={descId} className="mt-1 text-body-sm text-text-muted">
                  {description}
                </p>
              )}
            </div>
            {showCloseButton && (
              <div className="ml-auto flex min-w-0 flex-[0_1_auto] flex-wrap justify-start gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  data-ui="button"
                  className={buttonVariants({
                    variant: 'ghost',
                    size: 'compact',
                    className: 'w-8 p-0',
                  })}
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && (
          <div className="flex flex-wrap min-h-control shrink-0 items-center justify-end gap-3 [&_button]:min-h-control border-t border-border bg-surface-soft px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  if (typeof window === 'undefined') return null;
  return createPortal(content, document.body);
}
